import React, { FC, useEffect, useState } from "react";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import closeIcon from "../../images/close.svg";
import { CertificateDetailsType } from "../../models/CertificateType";
import { getCertificateDetailsById } from "../../services/securityPolicyService";
import Card from "../card/card";
import CardBody from "../card/card-body";
import CardHeader from "../card/card-header";
import { toast } from "react-toastify";
import ActivityFrame from "../activity-frame/activity-frame";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

interface CertificateDetailsProps {
    onExportCertificate?: (cert: CertificateDetailsType) => void;
    onRecreateCertificate?: (cert: CertificateDetailsType) => void;
    onRevokeCertificate?: (cert: CertificateDetailsType) => void;
    onClose: () => void;
    id: number | undefined;
}

const CertificateDetails: FC<CertificateDetailsProps> = ({ id, onExportCertificate, onRecreateCertificate, onRevokeCertificate, onClose }) => {
    const { translate } = useLocale();
    const [details, setDetails] = useState<CertificateDetailsType>({} as CertificateDetailsType);
    const [busy, setBusy] = useState<boolean>(false);

    useEffect(() => {
        getData();
    }, [id]);

    const getData = async () => {
        setBusy(true);
        try {
            if (id) {
                const response = await getCertificateDetailsById(id, translate);
                setDetails(response.data);
            }
        } catch (error) {
            toast.error(translate.byKey(TranslationKeys.gettingCertificateDetailsError));
        }
        setBusy(false);
    }

    return (
        <div className={'wizard relative'} id='wizard'>
            <ActivityFrame busy={busy} className='h-full flex flex-col flex-grow bg-white' >
                <Card style={{ maxHeight: 560 }} className="h-full flex flex-col">
                    <CardHeader className={`flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                        <div className="flex flex-row">
                            <p className="pl-3 font-bold font text-white">{details.name}</p>
                        </div>
                        <img src={closeIcon} onClick={onClose} className="cursor-pointer mr-2" id='closeWizardIcon' />
                    </CardHeader>
                    <div className={'h-full'} style={(onExportCertificate || onRecreateCertificate || onRevokeCertificate) && { maxHeight: '85%' }}>
                        <CardBody className={'p-3 h-full w-full'}>
                            <div className="flex flex-col h-full w-full overflow-y-auto">

                                {/* Only shows if the certificate has been revoked */}
                                {details.isRevoked && <div className="p-1 mb-4">
                                    <FontAwesomeIcon color={"red"} icon={faTriangleExclamation} />
                                    <span className='pl-1 ml-px text-sm text-red-600'>{translate.byKey(TranslationKeys.certificateRevoked)}</span>
                                </div>}

                                <div className="mb-4">
                                    <h4 className="p-1 font-bold mb-1">{translate.byKey(TranslationKeys.issuedTo)}:</h4>
                                    <div className="p-1 grid grid-cols-2 gap-y-1">
                                        <div>{translate.byKey(TranslationKeys.commonName)}:</div>
                                        <div>{details.commonName}</div>

                                        <div>{translate.byKey(TranslationKeys.organisation)}:</div>
                                        <div>{details.organisation}</div>

                                        <div>{translate.byKey(TranslationKeys.organisationalUnit)}:</div>
                                        <div>{details.organisationalUnit}</div>
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <h4 className="p-1 font-bold mb-1">{translate.byKey(TranslationKeys.issuer)}:</h4>
                                    <div className="p-1 grid grid-cols-2 gap-y-1">
                                        <div>{translate.byKey(TranslationKeys.commonName)}:</div>
                                        <div>{details.issuerCommonName}</div>

                                        <div>{translate.byKey(TranslationKeys.organisation)}:</div>
                                        <div>{details.issuerOrganisation}</div>

                                        <div>{translate.byKey(TranslationKeys.organisationalUnit)}:</div>
                                        <div>{details.issuerOrganisationalUnit}</div>
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <h4 className="p-1 font-bold mb-1">{translate.byKey(TranslationKeys.validityPeriod)}</h4>
                                    <div className="p-1 grid grid-cols-2 gap-y-1">
                                        <div>{translate.byKey(TranslationKeys.issuedOn)}:</div>
                                        <div>{details.issuedOn}</div>

                                        <div>{translate.byKey(TranslationKeys.expiresOn)}:</div>
                                        <div>{details.expiresOn}</div>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="p-1 font-bold mb-1">
                                        {details.certificateFingerprintAlgorithm}<br />
                                        {translate.byKey(TranslationKeys.fingerprints)}
                                    </h4>
                                    <div className="p-1 grid grid-cols-2 gap-y-1">
                                        <div>{translate.byKey(TranslationKeys.certificate)}:</div>
                                        <div>{details.certificateFingerprintModulus}</div>

                                        <div>{translate.byKey(TranslationKeys.publicKey)}:</div>
                                        <div className="break-all whitespace-pre-wrap">{details.publicKeyFingerprintModulus}</div>
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                        {(onExportCertificate || onRecreateCertificate || onRevokeCertificate) &&
                            <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
                                <div className="flex flex-row mt-4 space-x-2">
                                    {!details.isRevoked && onRevokeCertificate &&
                                        <button
                                            className="py-1 px-5 warning secondary warning-enabled"
                                            type="button"
                                            onClick={() => onRevokeCertificate(details)}>{translate.byKey(TranslationKeys.revoke)}
                                        </button>
                                    }
                                    {onExportCertificate &&
                                        <button
                                            className="py-1 px-5 secondary secondary-enabled"
                                            type="button"
                                            onClick={() => onExportCertificate(details)}>{translate.byKey(TranslationKeys.export)}
                                        </button>
                                    }
                                    {!details.isRevoked && onRecreateCertificate &&
                                        <button
                                            className="py-1 px-5 secondary secondary-enabled"
                                            type="button"
                                            onClick={() => onRecreateCertificate(details)}>{translate.byKey(TranslationKeys.recreate)}
                                        </button>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                </Card>
            </ActivityFrame>
        </div>
    )
}

export default CertificateDetails;
